import { Button, HStack, Stack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSend } from "react-icons/fi";
import { TipTap } from "../../input/TipTap/TipTap";
import { AttachmentDocumentOption, AttachmentPicker } from "./AttachmentPicker";
import { richTextHasContent } from "./communicationUtils";
import { useMessageAttachments } from "./useMessageAttachments";

interface Props {
  attachments: ReturnType<typeof useMessageAttachments>;
  requestDocuments: AttachmentDocumentOption[];
  offerDocuments?: AttachmentDocumentOption[];
  designContest?: boolean;
  isSending: boolean;
  onSend: (content: string) => Promise<boolean>;
  onDraftChange?: (content: string) => void;
}

export const ClarificationReplyForm = ({
  attachments,
  requestDocuments,
  offerDocuments,
  designContest = false,
  isSending,
  onSend,
  onDraftChange,
}: Props) => {
  const { t } = useTranslation();
  const [content, setContent] = useState("");
  const [resetKey, setResetKey] = useState(0);

  const canSend =
    richTextHasContent(content) && !attachments.isUploading && !attachments.hasFailedUploads;

  const handleSend = async () => {
    if (!canSend) return;
    const sent = await onSend(content);
    if (!sent) return;
    setContent("");
    onDraftChange?.("");
    attachments.reset();
    setResetKey((key) => key + 1);
  };

  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
      <TipTap ariaLabel={t("Message") ?? ""}
        key={resetKey}
        content=""
        editable
        debounceWait={0}
        onUpdate={(value) => {
          setContent(value);
          onDraftChange?.(value);
        }}
      />
      <AttachmentPicker
        controls={attachments}
        requestDocuments={requestDocuments}
        offerDocuments={offerDocuments}
        designContest={designContest}
      />
      <HStack justify="flex-end">
        <Button
          size="sm"
          colorScheme="teal"
          leftIcon={<FiSend />}
          onClick={handleSend}
          isLoading={isSending}
          isDisabled={!canSend}
        >
          {t("Send message")}
        </Button>
      </HStack>
    </Stack>
  );
};
